/** common */
import React, { useEffect, useRef, useState } from "react";
import { CSSTransition } from "react-transition-group";

/** store */
import useChatbotAPIStore from "apis/chatbot/chatbot.api";
import useChatbotStore from "stores/chatbot/useChatbotStore";

/** hook */
import { useLocation } from "react-router-dom";

/** style */
import { StyledChatbotPopup } from "./style";

/** component */
import { Button } from "components/button/Button/Button";
import { Icon } from "components/icon/Icon";
import { demoData } from "./demo/demoData";
import useProjectDataStore from "stores/useProjectDataStore";
import { useAddStep } from "containers/Project/Hooks/useAddStep";
import useStepsStore from "stores/project/data/steps/useStepsStore";
import { chatbotUtil } from "utils/chatbot";
import { ReportConfig } from "containers/Report/Config/ReportConfig";
import { getTranslation } from "utils/translation";
import { ChatItemType } from "types/chatbot/chatbot";
import { ChatbotBody } from "./body/ChatbotBody";

const HANDLE_LIST = [
  "top-handle",
  "left-handle",
  "right-handle",
  "bottom-handle",
  "top-left-handle",
  "top-right-handle",
  "bottom-left-handle",
  "bottom-right-handle",
];

const CURSOR_OPTIONS: { [key in (typeof HANDLE_LIST)[number]]: string } = {
  "top-handle": "ew-resize",
  "left-handle": "ns-resize",
  "right-handle": "ew-resize",
  "bottom-handle": "ns-resize",
  "top-left-handle": "nwse-resize",
  "top-right-handle": "nesw-resize",
  "bottom-left-handle": "nesw-resize",
  "bottom-right-handle": "nwse-resize",
};

const ChatbotPopup = () => {
  /** api */
  const { getChatbotResponse } = useChatbotAPIStore();

  /** hook */
  const location = useLocation();
  const { addStep } = useAddStep();

  /** store */
  const { isChatbotOpen, setIsChatbotOpen } = useChatbotStore();
  const { projectData } = useProjectDataStore();
  const { saveStepsTrigger } = useStepsStore();

  /** state */
  const [isInit, setIsInit] = useState(true);
  const [chatList, setChatList] = useState<ChatItemType[]>([
    {
      type: "date",
      createdAt: new Date().toISOString(),
    },
    {
      ...ReportConfig.common.AI_DEFAULT_CHAT,
      createdAt: new Date().toISOString(),
    },
  ]);

  /** ref */
  // 챗봇 팝업 위치
  const popupPosition = useRef({
    x: 0,
    y: 0,
    width: 400,
    height: 500,
  });

  // 챗봇 팝업 요소
  const popupRef = useRef<HTMLDivElement>(null);
  // 챗봇 응답 요청 취소 컨트롤러
  const abortControllerRef = useRef<AbortController | null>(null);

  const extractJsonBlock = (text: string) => {
    const match = text.match(/```json[\s\S]*?```/);
    return match ? match[0] : ""; // match[0]은 전체 매치 (```json 포함)
  };

  /** function */
  /**
   * @description 챗봇 응답 요청 취소 컨트롤러 초기화
   */
  const abortRequest = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
  };

  /** event handler */
  /**
   * @description 챗봇 팝업을 드래그하여 이동시키기 위한 이벤트
   * @param e
   */
  const onMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    e.preventDefault();

    // 대상의 현재 x, y 좌표와 마우스 커서의 x, y 좌표의 차이
    const offset = {
      x: e.clientX - popupPosition.current.x,
      y: e.clientY - popupPosition.current.y,
    };

    const onMouseMove = (e: MouseEvent) => {
      const popupWidth = popupRef.current?.offsetWidth ?? 1;
      const popupHeight = popupRef.current?.offsetHeight ?? 1;

      // 화면 바깥으로 나갈 수 없도록 제한
      popupPosition.current = {
        ...popupPosition.current,
        x: Math.max(
          Math.min(e.clientX - offset.x, window.innerWidth - popupWidth / 2),
          popupWidth / 2
        ),
        y: Math.max(
          Math.min(e.clientY - offset.y, window.innerHeight - popupHeight / 2),
          popupHeight / 2
        ),
      };

      // 스타일 업데이트
      if (popupRef.current) {
        popupRef.current.style.left = `${popupPosition.current.x}px`;
        popupRef.current.style.top = `${popupPosition.current.y}px`;
      }
    };

    // 마우스 이벤트 제거
    const onMouseUp = () => {
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", onMouseUp);
    };

    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup", onMouseUp);
  };

  /**
   * @description 챗봇에 질문 후 응답 생성
   */
  const handleSendMessage = async (
    message: string
  ): Promise<ChatItemType | undefined> => {
    try {
      if (
        ["tcms", "공구", "필요"].every((keyword) =>
          message.toLowerCase().includes(keyword)
        )
      ) {
        await new Promise((resolve) => setTimeout(resolve, 2000));

        return {
          type: "assistant",
          content: await chatbotUtil.getSanitizedMessage(demoData[0]),
          originalText: demoData[0],
          status: "success",
          createdAt: new Date().toISOString(),
        };
      } else if (
        ["공구", "이미지"].every((keyword) =>
          message.toLowerCase().includes(keyword)
        )
      ) {
        await new Promise((resolve) => setTimeout(resolve, 2000));

        return {
          type: "assistant",
          content: await chatbotUtil.getSanitizedMessage(demoData[1]),
          originalText: demoData[1],
          status: "success",
          createdAt: new Date().toISOString(),
        };
      } else {
        const controller = new AbortController();
        abortControllerRef.current = controller;

        const response = await getChatbotResponse(
          message,
          controller.signal,
          projectData?.projectId
            ? {
                projectVersion: projectData.projectVersion,
                projectId: projectData.projectId,
              }
            : undefined
        );

        if (!response || !response.isSuccess) throw new Error(response.error);

        if (projectData?.projectId)
          saveStep(
            response.resultList ? extractJsonBlock(response.resultList) : ""
          );

        const sanitizedMessage = await chatbotUtil.getSanitizedMessage(
          response.resultList
        );

        // AI 응답 메시지를 메시지 리스트에 추가
        return {
          type: "assistant",
          content: sanitizedMessage,
          originalText: response.resultList,
          status: "success",
          createdAt: new Date().toISOString(),
        };
      }
    } catch (e) {
      if (e instanceof Error && e.message === "AbortError") {
        console.error("API 호출이 취소되었습니다.");
      } else {
        return {
          type: "assistant",
          content: getTranslation("message", "ERROR.RESPONSE_ERROR_TRY_AGAIN"),
          originalText: getTranslation(
            "message",
            "ERROR.RESPONSE_ERROR_TRY_AGAIN"
          ),
          status: "error",
          createdAt: new Date().toISOString(),
        };
      }
    }
  };

  const saveStep = (responseMsg: string) => {
    addStep("chat", undefined, responseMsg);

    saveStepsTrigger();
  };

  /**
   * @description 챗봇 팝업 크기 조절 이벤트
   * @param e
   */
  const onHandleMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    e.preventDefault();

    const resizingTarget = e.currentTarget.id;

    const startX = e.clientX;
    const startY = e.clientY;

    // 팝업 크기를 조정하는 도중 마우스가 벗어나도 커서 모양 유지
    const style = document.createElement("style");
    style.innerHTML = `
      body { 
        cursor: ${CURSOR_OPTIONS[resizingTarget]} !important; 
      }
    `;
    document.head.appendChild(style);

    const initialPosition = {
      x: popupPosition.current.x,
      y: popupPosition.current.y,
      width: popupPosition.current.width,
      height: popupPosition.current.height,
    };

    const onMouseMove = (e: MouseEvent) => {
      const deltaX = e.clientX - startX;
      const deltaY = e.clientY - startY;

      const MIN_SIZE = 250;
      const MAX_SIZE = 800;

      const isInRange = (size: number) => size >= MIN_SIZE && size <= MAX_SIZE;

      const updatePosition = (newWidth?: number, newHeight?: number) => {
        const position = { ...popupPosition.current };

        if (newWidth && isInRange(newWidth)) {
          position.width = newWidth;
          position.x = initialPosition.x + deltaX / 2;
        }

        if (newHeight && isInRange(newHeight)) {
          position.height = newHeight;
          position.y = initialPosition.y + deltaY / 2;
        }

        popupPosition.current = position;
      };

      switch (resizingTarget) {
        case "top-handle":
          updatePosition(undefined, initialPosition.height - deltaY);
          break;

        case "left-handle":
          updatePosition(initialPosition.width - deltaX);
          break;

        case "right-handle":
          updatePosition(initialPosition.width + deltaX);
          break;

        case "bottom-handle":
          updatePosition(undefined, initialPosition.height + deltaY);
          break;

        case "top-left-handle":
          updatePosition(
            initialPosition.width - deltaX,
            initialPosition.height - deltaY
          );
          break;

        case "top-right-handle":
          updatePosition(
            initialPosition.width + deltaX,
            initialPosition.height - deltaY
          );
          break;

        case "bottom-left-handle":
          updatePosition(
            initialPosition.width - deltaX,
            initialPosition.height + deltaY
          );
          break;

        case "bottom-right-handle":
          updatePosition(
            initialPosition.width + deltaX,
            initialPosition.height + deltaY
          );
          break;
      }

      if (popupRef.current) {
        popupRef.current.style.left = `${popupPosition.current.x}px`;
        popupRef.current.style.top = `${popupPosition.current.y}px`;
        popupRef.current.style.width = `${popupPosition.current.width}px`;
        popupRef.current.style.height = `${popupPosition.current.height}px`;
      }
    };

    const onMouseUp = () => {
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", onMouseUp);
      document.head.removeChild(style);
    };

    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup", onMouseUp);
  };

  const onResize = () => {
    if (isChatbotOpen) {
      if (popupRef.current) {
        const popupRect = popupRef.current.getBoundingClientRect();
        const popupWidth = popupRect.width;
        const popupHeight = popupRect.height;

        if (
          popupRect.left < 0 ||
          popupRect.left > window.innerWidth ||
          popupRect.top < 0 ||
          popupRect.top > window.innerHeight
        ) {
          popupPosition.current = {
            ...popupPosition.current,
            x: popupWidth / 2 + 100,
            y: popupHeight / 2 + 100,
          };

          if (popupRef.current) {
            popupRef.current.style.left = `${popupPosition.current.x}px`;
            popupRef.current.style.top = `${popupPosition.current.y}px`;
          }
        }
      }
    }
  };

  useEffect(() => {
    if (isChatbotOpen && isInit) {
      popupPosition.current = {
        x: window.innerWidth * 0.85,
        y: window.innerHeight * 0.7,
        width: 400,
        height: 500,
      };

      if (popupRef.current) {
        popupRef.current.style.left = `${popupPosition.current.x}px`;
        popupRef.current.style.top = `${popupPosition.current.y}px`;
        popupRef.current.style.width = `${popupPosition.current.width}px`;
        popupRef.current.style.height = `${popupPosition.current.height}px`;
      }
      setIsInit(false);
      window.addEventListener("resize", onResize);
    }

    return () => {
      window.removeEventListener("resize", onResize);
    };
  }, [isChatbotOpen]);

  useEffect(() => {
    setIsChatbotOpen(false);
  }, [location]);

  return (
    <CSSTransition
      in={isChatbotOpen}
      timeout={500}
      classNames="popup-chatbot"
      mountOnEnter
    >
      <StyledChatbotPopup ref={popupRef}>
        {HANDLE_LIST.map((handle) => (
          <div
            className={handle}
            id={handle}
            key={handle}
            onMouseDown={onHandleMouseDown}
          ></div>
        ))}
        <header onMouseDown={onMouseDown}>
          <h1>
            <Icon
              icon="AI01"
              size={16}
              strokeWidth={1}
              stroke="--text-gray-normal"
              fill="--text-gray-normal"
            />
            {getTranslation("resource", "AI_ASSISTANT")}
          </h1>
          <Button
            type="text"
            preIcon="XCloseLg"
            color="normal"
            size="small"
            onClick={() => setIsChatbotOpen(false)}
          />
        </header>
        <ChatbotBody
          activeClearHistory
          chatList={chatList}
          setChatList={setChatList}
          height="calc(100% - 4rem)"
          handleSendMessage={handleSendMessage}
          abortRequest={abortRequest}
        />
      </StyledChatbotPopup>
    </CSSTransition>
  );
};

export default ChatbotPopup;
